import { useEffect, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { toast } from "sonner"
import { useHasPermission } from "./session-context"
import { Select } from "./components/select"
import { Modal, ModalActions, ModalButton } from "./components/modal"
import { useConfirm } from "./components/confirm-modal"
import { ContentPagination } from "./content-components"

type SubmissionStatus = "new" | "reviewed" | "archived"
const PAGE_SIZE = 10

interface FormField {
  id: string
  type: string
  label: string
  name: string
  placeholder?: string
  required: boolean
  options?: string[]
  position: number
}

interface Form {
  id: string
  code: string
  name: string
  description: string | null
  status: "draft" | "published" | "archived"
  fields: FormField[] | null
  submitButtonText: string
  successMessage: string
  submissionCount: number
  publishedAt: string | null
  createdAt: string
  updatedAt: string
}

interface Submission {
  id: string
  code: string
  formId: string
  data: Record<string, string | boolean>
  status: SubmissionStatus
  submittedAt: string
  reviewedAt: string | null
  createdAt: string
  updatedAt: string
}

interface SubmissionListItem {
  id: string
  code: string
  status: SubmissionStatus
  preview: string
  submittedAt: string
}

const STATUS_COLORS: Record<SubmissionStatus, { bg: string; text: string }> = {
  new: { bg: "bg-blue-100", text: "text-blue-700" },
  reviewed: { bg: "bg-green-100", text: "text-green-700" },
  archived: { bg: "bg-gray-100", text: "text-gray-700" },
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  )
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export function FormSubmissionsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const canView = useHasPermission("forms")
  const canMutate = useHasPermission("forms_mutate")

  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState<Form | null>(null)
  const [submissions, setSubmissions] = useState<SubmissionListItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState("")
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null)
  const { confirm, modal } = useConfirm()

  const clearSubmissionParam = () => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete("submissionId")
    setSearchParams(nextParams, { replace: true })
  }

  useEffect(() => {
    if (id) void fetchFormAndSubmissions()
  }, [id, page, statusFilter])

  const fetchFormAndSubmissions = async () => {
    try {
      const [formRes, subsRes] = await Promise.all([
        client.api.admin.forms[":id"].$get({ param: { id: id! } }),
        client.api.admin.forms[":id"].submissions.$get({
          param: { id: id! },
          query: {
            status: statusFilter ? statusFilter as SubmissionStatus : undefined,
            limit: String(PAGE_SIZE),
            offset: String((page - 1) * PAGE_SIZE),
          },
        }),
      ])

      const formBody = await formRes.json()
      const subsBody = await subsRes.json()

      if (formRes.ok && "data" in formBody) {
        setForm(formBody.data as Form)
      } else {
        toast.error("Form not found")
        navigate("/admin/forms")
        return
      }

      if (subsRes.ok && "data" in subsBody) {
        setSubmissions(subsBody.data.items as SubmissionListItem[])
        setTotal(subsBody.data.total)
      }
    } catch {
      toast.error("Failed to load submissions")
      navigate("/admin/forms")
    } finally {
      setLoading(false)
    }
  }

  const fetchSubmissionDetail = async (subId: string) => {
    try {
      const res = await client.api.admin.forms[":id"].submissions[":subId"].$get({
        param: { id: id!, subId },
      })
      const body = await res.json()
      if (res.ok && "data" in body) {
        setSelectedSubmission(body.data as Submission)
      }
    } catch {
      toast.error("Failed to load submission")
    }
  }

  useEffect(() => {
    setPage(1)
  }, [id, statusFilter])

  useEffect(() => {
    const submissionId = searchParams.get("submissionId")
    if (form && submissionId) void fetchSubmissionDetail(submissionId)
  }, [form, searchParams])

  const handleStatusChange = async (subId: string, newStatus: SubmissionStatus) => {
    try {
      const res = await client.api.admin.forms[":id"].submissions[":subId"].$patch({
        param: { id: id!, subId },
        json: { status: newStatus },
      })
      const body = await res.json()
      if (res.ok && "data" in body) {
        toast.success(`Status updated to ${newStatus}`)
        setSelectedSubmission(body.data as Submission)
        // Refresh list
        fetchFormAndSubmissions()
      } else {
        toast.error(getErrorMessage(body))
      }
    } catch {
      toast.error("Failed to update status")
    }
  }

  const handleDelete = async (subId: string) => {
    const confirmed = await confirm({
      title: "Delete submission",
      message: "Are you sure you want to delete this submission? This cannot be undone.",
      confirmLabel: "Delete submission",
    })
    if (!confirmed) return

    try {
      const res = await client.api.admin.forms[":id"].submissions[":subId"].$delete({
        param: { id: id!, subId },
      })
      if (res.ok) {
        toast.success("Submission deleted")
        setSelectedSubmission(null)
        clearSubmissionParam()
        fetchFormAndSubmissions()
      } else {
        const body = await res.json()
        toast.error(getErrorMessage(body))
      }
    } catch {
      toast.error("Failed to delete submission")
    }
  }

  const filteredSubmissions = submissions.filter((s) => !statusFilter || s.status === statusFilter)

  if (!canView) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Submissions</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to view submissions.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(`/admin/forms/builder/${id}`)}
          className="rounded-lg border border-gold-500 p-2 hover:bg-cream-100"
        >
          <ChevronLeftIcon className="h-5 w-5 text-ink" />
        </button>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-3xl">
            Submissions
          </h1>
          <p className="mt-1 text-sm text-ink/60">
            {form?.name} • {form?.code}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="w-40">
          <Select
            value={statusFilter ? statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1) : "All statuses"}
            onChange={(value) => setStatusFilter(value === "All statuses" ? "" : value.toLowerCase())}
            options={["All statuses", "New", "Reviewed", "Archived"]}
          />
        </div>
      </div>

      {/* Submissions Table */}
      {filteredSubmissions.length === 0 ? (
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-ink/30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
          </svg>
          <h3 className="mt-4 text-lg font-semibold text-ink">No submissions</h3>
          <p className="mt-2 text-sm text-ink/60">
            No submissions match your filters.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-[10px] border border-gold-500 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gold-500/50 bg-cream-50">
                  <th className="px-4 py-4 text-left text-sm font-bold text-ink">Code</th>
                  <th className="px-4 py-4 text-left text-sm font-bold text-ink">Preview</th>
                  <th className="px-4 py-4 text-left text-sm font-bold text-ink">Status</th>
                  <th className="px-4 py-4 text-left text-sm font-bold text-ink">Submitted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10">
                {filteredSubmissions.map((sub) => (
                  <tr
                    key={sub.id}
                    onClick={() => fetchSubmissionDetail(sub.id)}
                    className="cursor-pointer hover:bg-cream-50"
                  >
                    <td className="px-4 py-4 text-sm font-medium text-ink">
                      {sub.code}
                    </td>
                    <td className="px-4 py-4 text-sm text-ink/70">
                      {sub.preview || "—"}
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${
                          STATUS_COLORS[sub.status].bg
                        } ${STATUS_COLORS[sub.status].text}`}
                      >
                        {sub.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-ink/50">
                      {formatDate(sub.submittedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ContentPagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} disabled={loading} />

      {/* Submission Detail Modal */}
      <SubmissionDetailModal
        submission={selectedSubmission}
        form={form}
        onClose={() => {
          setSelectedSubmission(null)
          clearSubmissionParam()
        }}
        canMutate={canMutate}
        onStatusChange={handleStatusChange}
        onDelete={handleDelete}
      />
      {modal}
    </div>
  )
}

function SubmissionDetailModal({
  submission,
  form,
  onClose,
  canMutate,
  onStatusChange,
  onDelete,
}: {
  submission: Submission | null
  form: Form | null
  onClose: () => void
  canMutate: boolean
  onStatusChange: (subId: string, status: SubmissionStatus) => void
  onDelete: (subId: string) => void
}) {
  if (!submission || !form) return null

  const fields = form.fields || []

  return (
    <Modal
      isOpen={!!submission}
      onClose={onClose}
      title={submission.code}
      subtitle={`Submitted ${formatDateTime(submission.submittedAt)}`}
      maxWidth="lg"
    >
      <div className="space-y-6">
        {/* Status */}
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold capitalize ${
              STATUS_COLORS[submission.status].bg
            } ${STATUS_COLORS[submission.status].text}`}
          >
            {submission.status}
          </span>
          {submission.reviewedAt && (
            <span className="text-sm text-ink/50">
              Reviewed {formatDateTime(submission.reviewedAt)}
            </span>
          )}
        </div>

        {/* Submission Data */}
        <div className="rounded-lg border border-gold-500/50 bg-cream-50 p-4">
          <h3 className="mb-3 text-sm font-semibold text-ink">Response</h3>
          <div className="space-y-3">
            {fields.length > 0 ? (
              fields.map((field) => {
                const value = submission.data[field.name]
                return (
                  <div key={field.id}>
                    <p className="text-xs font-medium text-ink/50">{field.label}</p>
                    <p className="mt-0.5 text-sm text-ink">
                      {field.type === "checkbox"
                        ? (value ? "Yes" : "No")
                        : (value?.toString() || "—")}
                    </p>
                  </div>
                )
              })
            ) : (
              // If form has no fields defined, show raw data
              Object.entries(submission.data).map(([key, value]) => (
                <div key={key}>
                  <p className="text-xs font-medium text-ink/50">{key}</p>
                  <p className="mt-0.5 text-sm text-ink">
                    {typeof value === "boolean" ? (value ? "Yes" : "No") : (value || "—")}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Status Actions */}
        {canMutate && (
          <div>
            <h3 className="mb-2 text-sm font-medium text-ink">Update Status</h3>
            <div className="flex flex-wrap gap-2">
              {(["new", "reviewed", "archived"] as const).map((status) => (
                <button
                  key={status}
                  onClick={() => onStatusChange(submission.id, status)}
                  disabled={submission.status === status}
                  className={`rounded-full px-4 py-2 text-sm font-medium capitalize transition-colors disabled:opacity-50 ${
                    submission.status === status
                      ? `${STATUS_COLORS[status].bg} ${STATUS_COLORS[status].text}`
                      : "border border-ink/20 text-ink hover:bg-cream-100"
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        )}

        <ModalActions>
          {canMutate && (
            <ModalButton onClick={() => onDelete(submission.id)} variant="danger">
              Delete
            </ModalButton>
          )}
          <ModalButton onClick={onClose}>Close</ModalButton>
        </ModalActions>
      </div>
    </Modal>
  )
}
